import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  CalendarDays,
  Check,
  ChevronDown,
  ClipboardList,
  Copy,
  FileImage,
  FileSpreadsheet,
  FileText,
  History,
  Moon,
  Plus,
  RefreshCw,
  Save,
  Settings2,
  Sun,
  Trash2,
  UserRound,
  FileUp,
  X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  buildShiftLibrary,
  DEFAULT_SCHEDULE_SETTINGS,
  SHIFT_OPTIONS,
  addDays,
  buildAssignmentKey,
  buildFortnightDays,
  formatScheduleTimeRange,
  getAssignmentByKey,
  getEntryDefinition,
  getFortnightEndDate,
  isOffCode,
  normalizeShiftOptions,
  normalizeScheduleSettings,
  summarizeOwnAssignments,
} from '../utils/schedules';
import { formatDateDisplay, normalizeDateForStorage, parseFlexibleDate, todayAsStorageDate } from '../utils/date';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const createScheduleTitle = (startDate, endDate) =>
  `Quincena ${formatDateDisplay(startDate)} - ${formatDateDisplay(endDate)}`;

const getRoleLabel = (role) => {
  switch ((role || '').toLowerCase()) {
    case 'admin':
      return 'Administrador';
    case 'office':
    case 'oficina':
      return 'Oficina';
    case 'collaborator':
      return 'Colaborador';
    default:
      return role || '';
  }
};

const isCollaboratorRole = (role) => ['collaborator', 'colaborador'].includes((role || '').toLowerCase());

const getDefaultSchedulePayload = (currentUser) => {
  const startDate = todayAsStorageDate();
  const endDate = getFortnightEndDate(startDate);

  return {
    title: createScheduleTitle(startDate, endDate),
    startDate,
    endDate,
    mode: 'create',
    baseId: '',
    createdBy: currentUser?.id || '',
    createdByName: currentUser?.name || '',
  };
};

const deepClone = (value) => JSON.parse(JSON.stringify(value));
const DEFAULT_SHIFT_LIBRARY = buildShiftLibrary(SHIFT_OPTIONS);
const DEFAULT_SHIFT_CODES = new Set(SHIFT_OPTIONS.map((option) => option.code));

const createCustomShiftOption = () => ({
  code: `custom_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  label: 'Nuevo registro',
  shortLabel: 'Nuevo',
  category: 'shift',
  startTime: '08:00',
  endTime: '17:00',
  color: '#2563eb',
  bgClass: 'bg-blue-100 text-blue-800 border-blue-200',
  enabled: true,
});

const IMPORT_DAY_NAMES = ['Dom', 'Lun', 'Mar', 'Mi?', 'Jue', 'Vie', 'S?b'];

const normalizeImportText = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');

const extractImportedDate = (value) => {
  const direct = parseFlexibleDate(value);
  if (direct) return direct;

  const text = String(value ?? '');
  const match = text.match(/(\d{1,2}[\/.-]\d{1,2}[\/.-]\d{4}|\d{4}[\/.-]\d{1,2}[\/.-]\d{1,2})/);
  return match ? parseFlexibleDate(match[1]) : null;
};

const buildImportUserLookup = (users = []) =>
  users.reduce((acc, user) => {
    const aliases = [
      user?.id,
      user?.name,
      ...(Array.isArray(user?.aliases) ? user.aliases : []),
      ...(user?.sourceMappings ? Object.values(user.sourceMappings).flatMap((entry) => (Array.isArray(entry) ? entry : [entry])) : []),
      user?.email,
    ].filter(Boolean);

    aliases.forEach((alias) => {
      acc[normalizeImportText(alias)] = user;
    });

    return acc;
  }, {});

const buildImportShiftLookup = (shiftOptions = []) =>
  shiftOptions.reduce((acc, option) => {
    const aliases = [
      option?.code,
      option?.label,
      option?.shortLabel,
      option?.startTime && option?.endTime ? formatScheduleTimeRange(option.startTime, option.endTime) : '',
      option?.startTime && option?.endTime ? `${option.startTime} - ${option.endTime}` : '',
      option?.startTime && option?.endTime ? `${option.startTime} a ${option.endTime}` : '',
    ].filter(Boolean);

    const codeAliases = {
      day_5_7: ['5am-6pm', '5 am 6 pm', '5am 6pm', '5:00 a. m. - 6:00 p. m.'],
      day_6_8: ['8am-9pm', '8 am 9 pm', '8am 9pm', '8:00 a. m. - 9:00 p. m.'],
      day_7_9: ['7am-9pm', '7 am 9 pm', '7am 9pm', '7:00 a. m. - 9:00 p. m.'],
      night_6_6: ['nocturno 6pm-6am', 'nocturno 6 pm 6 am', '6pm-6am', '6 pm 6 am'],
      free: ['libre'],
      sunday: ['domingo', 'domingo libre'],
      compensatory_rest: ['descanso', 'descanso post-nocturno', 'post-nocturno'],
      unassigned: ['sin asignar'],
      vacation: ['vacaciones'],
      medical_leave: ['incapacidad'],
      permission: ['permiso'],
    };

    if (codeAliases[option?.code]) {
      aliases.push(...codeAliases[option.code]);
    }

    aliases.forEach((alias) => {
      acc[normalizeImportText(alias)] = option;
    });

    return acc;
  }, {});

const parseImportedAssignment = (rawValue, shiftOptions = [], shiftLookup = {}) => {
  const text = typeof rawValue === 'string' ? rawValue.trim() : String(rawValue ?? '').trim();
  const normalized = normalizeImportText(text);

  if (!normalized || ['sin asignar', 'asignar', '-', '?', 'n/a', 'na'].includes(normalized)) {
    return null;
  }

  const exactMatch = shiftLookup[normalized];
  if (exactMatch) {
    const timeMatch = text.match(/(\d{1,2}:\d{2})\s*[-–-]\s*(\d{1,2}:\d{2})/);
    const isTimeRange = !!timeMatch;

    return {
      code: exactMatch.code,
      label: text || exactMatch.label,
      category: exactMatch.category,
      startTime: isTimeRange ? timeMatch[1] : (exactMatch.startTime || ''),
      endTime: isTimeRange ? timeMatch[2] : (exactMatch.endTime || ''),
      color: exactMatch.color || '',
    };
  }

  const timeMatch = text.match(/(\d{1,2}:\d{2})\s*[-–-]\s*(\d{1,2}:\d{2})/);
  if (timeMatch) {
    const matchedShift = shiftOptions.find(
      (option) => option?.startTime === timeMatch[1] && option?.endTime === timeMatch[2]
    );
    if (matchedShift) {
      return {
        code: matchedShift.code,
        label: text,
        category: matchedShift.category,
        startTime: matchedShift.startTime || timeMatch[1],
        endTime: matchedShift.endTime || timeMatch[2],
        color: matchedShift.color || '',
      };
    }
  }

  return null;
};

const buildImportedDays = (headerRow = []) =>
  headerRow
    .slice(1)
    .map((value) => extractImportedDate(value))
    .filter(Boolean)
    .map((date) => {
      const normalized = normalizeDateForStorage(date);
      return {
        date: normalized,
        label: formatDateDisplay(normalized),
        dayName: IMPORT_DAY_NAMES[date.getDay()],
        isSunday: date.getDay() === 0,
      };
    });


const getPreferredSchedule = (schedules = [], currentUser, isManager) => {
  if (!schedules.length) return null;

  const today = parseFlexibleDate(todayAsStorageDate());
  const matchesToday = schedules.find((schedule) => {
    const start = parseFlexibleDate(schedule.startDate);
    const end = parseFlexibleDate(schedule.endDate);
    if (!start || !end || !today) return false;
    return schedule.status === 'published' && today >= start && today <= end;
  });
  if (matchesToday) return matchesToday;

  const published = schedules.find((schedule) => schedule.status === 'published');
  if (published) return published;

  if (!isManager && currentUser?.id) {
    return null;
  }

  return schedules[0];
};

const getAssignmentDisplayText = (assignment, definition) => {
  if (!assignment) return 'Sin asignar';

  const timeRange = assignment.startTime && assignment.endTime
    ? formatScheduleTimeRange(assignment.startTime, assignment.endTime)
    : '';

  if (definition?.category === 'shift' || definition?.code === 'permission') {
    return timeRange || definition?.shortLabel || definition?.label || assignment.label || assignment.code || 'Asignado';
  }

  return definition?.label || assignment.label || assignment.code || 'Asignado';
};

const getAssignmentSecondaryText = (assignment, definition) => {
  if (!assignment) return 'Día no asignado';

  const timeRange = assignment.startTime && assignment.endTime
    ? formatScheduleTimeRange(assignment.startTime, assignment.endTime)
    : '';

  if (definition?.category === 'shift' || definition?.code === 'permission') {
    return timeRange || definition?.label || assignment.label || assignment.code || 'Asignado';
  }

  return definition?.label || assignment.label || assignment.code || 'Asignado';
};

const Schedules = () => {
  const { currentUser, users, isManager, fetchUsers } = useAuth();
  const { addNotification } = useNotification();
  const importInputRef = useRef(null);
  const activeUsers = users.filter((user) => user.active !== false);
  const [schedules, setSchedules] = useState([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState('');
  const [workingSchedule, setWorkingSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [statusFeedback, setStatusFeedback] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [headerExtrasVisible, setHeaderExtrasVisible] = useState(false);
  const [publishConfirm, setPublishConfirm] = useState({
    open: false,
    targetStatus: '',
    conflictingSchedule: null,
  });
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    schedule: null,
  });
  const [scheduleModal, setScheduleModal] = useState({
    open: false,
    title: '',
    startDate: '',
    endDate: '',
    mode: 'create',
    baseId: '',
  });
  const [editor, setEditor] = useState({
    open: false,
    user: null,
    day: null,
    code: 'day_5_7',
    startTime: DEFAULT_SHIFT_LIBRARY.day_5_7.startTime,
    endTime: DEFAULT_SHIFT_LIBRARY.day_5_7.endTime,
    notes: '',
  });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [legendExpanded, setLegendExpanded] = useState(true);
  const [globalConfig, setGlobalConfig] = useState({
    shiftOptions: normalizeShiftOptions(),
    updatedAt: '',
    updatedBy: '',
    updatedByName: '',
  });
  const [globalSettingsModal, setGlobalSettingsModal] = useState({
    open: false,
    shiftOptions: normalizeShiftOptions(),
  });
  const [settingsModal, setSettingsModal] = useState({
    open: false,
    user: null,
    settings: DEFAULT_SCHEDULE_SETTINGS,
  });

  const canManage = isManager;
  const shiftLibrary = useMemo(
    () => buildShiftLibrary(globalConfig.shiftOptions),
    [globalConfig.shiftOptions]
  );
  const enabledShiftOptions = useMemo(
    () => globalConfig.shiftOptions.filter((option) => option.enabled),
    [globalConfig.shiftOptions]
  );
  const availableShiftCodes = useMemo(
    () => enabledShiftOptions.map((option) => option.code),
    [enabledShiftOptions]
  );
  const getDefinition = (code) => getEntryDefinition(code, shiftLibrary);

  const fetchSchedules = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/schedules`);
      setSchedules(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching schedules:', error);
      addNotification('Horarios', 'No se pudieron cargar los horarios.', 'error');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchGlobalScheduleConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/schedule-config`);
      const normalized = normalizeShiftOptions(response.data?.shiftOptions);
      setGlobalConfig({
        shiftOptions: normalized,
        updatedAt: response.data?.updatedAt || '',
        updatedBy: response.data?.updatedBy || '',
        updatedByName: response.data?.updatedByName || '',
      });
      return normalized;
    } catch (error) {
      console.error('Error fetching schedule global config:', error);
      addNotification('Horarios', 'No se pudo cargar la configuración global de horarios.', 'warning');
      return normalizeShiftOptions();
    }
  };

  useEffect(() => {
    fetchSchedules();
    fetchGlobalScheduleConfig();
    if (!users.length) {
      fetchUsers();
    }
  }, []);

  useEffect(() => {
    if (!schedules.length) {
      setSelectedScheduleId('');
      setWorkingSchedule(null);
      return;
    }

    if (!selectedScheduleId) {
      const preferred = getPreferredSchedule(schedules, currentUser, isManager);
      if (preferred) {
        setSelectedScheduleId(preferred.id);
      }
      return;
    }

    const selected = schedules.find((schedule) => schedule.id === selectedScheduleId);
    if (selected) {
      setWorkingSchedule(deepClone(selected));
      return;
    }

    const fallback = getPreferredSchedule(schedules, currentUser, isManager);
    if (fallback) {
      setSelectedScheduleId(fallback.id);
    }
  }, [schedules, selectedScheduleId, currentUser, isManager]);

  const managerUsers = useMemo(() => {
    const sortedUsers = activeUsers
      .filter((user) => isCollaboratorRole(user.role))
      .sort((a, b) => a.name.localeCompare(b.name));
    if (!searchTerm.trim()) return sortedUsers;
    return sortedUsers.filter((user) =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [activeUsers, searchTerm]);

  const importableUsers = useMemo(
    () => activeUsers.filter((user) => isCollaboratorRole(user.role)),
    [activeUsers]
  );

  const importableUserLookup = useMemo(
    () => buildImportUserLookup(importableUsers),
    [importableUsers]
  );

  const importShiftLookup = useMemo(
    () => buildImportShiftLookup(enabledShiftOptions),
    [enabledShiftOptions]
  );

  const currentScheduleDays = useMemo(
    () => buildFortnightDays(workingSchedule?.startDate, workingSchedule?.endDate),
    [workingSchedule?.startDate, workingSchedule?.endDate]
  );

  const assignments = workingSchedule?.assignments || [];
  const assignmentMap = useMemo(() => getAssignmentByKey(assignments), [assignments]);

  const visibleUsers = canManage
    ? managerUsers
    : users.filter((user) => user.id === currentUser?.id && isCollaboratorRole(user.role));

  const ownAssignments = useMemo(() => {
    if (!currentUser?.id || !workingSchedule) return [];
    return (workingSchedule.assignments || []).filter((assignment) => assignment.userId === currentUser.id);
  }, [workingSchedule, currentUser?.id]);

  const ownScheduleSummary = useMemo(
    () => summarizeOwnAssignments(ownAssignments, currentScheduleDays),
    [ownAssignments, currentScheduleDays]
  );

  const currentWeekAssignments = useMemo(() => {
    if (!ownAssignments.length) return [];
    const today = parseFlexibleDate(todayAsStorageDate());
    if (!today) return [];

    const start = new Date(today);
    start.setDate(today.getDate() - today.getDay());
    const end = new Date(start);
    end.setDate(start.getDate() + 6);

    return ownAssignments.filter((assignment) => {
      const date = parseFlexibleDate(assignment.date);
      return date && date >= start && date <= end;
    });
  }, [ownAssignments]);

  const triggerImportSchedule = () => {
    importInputRef.current?.click();
  };

  const processImportedWorkbook = async (file) => {
    if (!file) return;

    setImporting(true);
    setHeaderExtrasVisible(true);

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
      const firstSheetName = workbook.SheetNames[0];
      if (!firstSheetName) {
        throw new Error('No se encontr? ninguna hoja en el archivo.');
      }

      const worksheet = workbook.Sheets[firstSheetName];
      const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
      if (!rows.length) {
        throw new Error('La hoja est? vac?a.');
      }

      const headerRowIndex = rows.findIndex((row) => {
        const firstCell = normalizeImportText(row?.[0]);
        return ['usuario', 'nombre', 'colaborador', 'empleado'].includes(firstCell);
      });

      const resolvedHeaderRowIndex = headerRowIndex >= 0 ? headerRowIndex : 0;
      const headerRow = rows[resolvedHeaderRowIndex] || [];
      const dataRows = rows.slice(resolvedHeaderRowIndex + 1).filter((row) =>
        Array.isArray(row) && row.some((cell) => String(cell ?? '').trim())
      );

      const importedDays = workingSchedule?.startDate && currentScheduleDays.length
        ? currentScheduleDays
        : buildImportedDays(headerRow);

      if (!importedDays.length) {
        throw new Error('No se pudieron interpretar las fechas del archivo.');
      }

      const importedAssignments = [];
      let unmatchedUsers = 0;
      let skippedCells = 0;
      const sampleSkipped = [];

      dataRows.forEach((row) => {
        const firstCell = String(row?.[0] ?? '').trim();
        const secondCell = String(row?.[1] ?? '').trim();
        if (!firstCell && !secondCell) return;

        const matchedUserById = importableUserLookup[normalizeImportText(firstCell)];
        const matchedUser = matchedUserById || importableUserLookup[normalizeImportText(firstCell)];
        if (!matchedUser) {
          unmatchedUsers += 1;
          return;
        }

        const dayOffset = matchedUserById ? 2 : 1;
        const userLabel = matchedUserById
          ? (secondCell || matchedUser.name || firstCell)
          : (firstCell || matchedUser.name || '');

        importedDays.forEach((day, index) => {
          const cellValue = row[index + dayOffset];
          const parsedAssignment = parseImportedAssignment(cellValue, enabledShiftOptions, importShiftLookup);
          if (!parsedAssignment) return;

          const validationError = validateAssignment(
            matchedUser,
            day,
            parsedAssignment.code,
            parsedAssignment.startTime,
            parsedAssignment.endTime
          );

          if (validationError) {
            skippedCells += 1;
            if (sampleSkipped.length < 5) {
              sampleSkipped.push(`${userLabel} ? ${day.label}: ${validationError}`);
            }
            return;
          }

          const definition = getDefinition(parsedAssignment.code);
          importedAssignments.push({
            userId: matchedUser.id,
            date: day.date,
            code: parsedAssignment.code,
            label: parsedAssignment.label || definition.label,
            category: parsedAssignment.category,
            startTime: parsedAssignment.startTime || '',
            endTime: parsedAssignment.endTime || '',
            color: parsedAssignment.color || definition.color,
            notes: '',
          });
        });
      });

      if (!importedAssignments.length) {
        throw new Error('No se encontraron asignaciones válidas para importar. Revisa que las celdas tengan turnos, estados o rangos horarios reconocibles.');
      }

      const summary = `Importadas ${importedAssignments.length} asignaciones${unmatchedUsers ? `, ${unmatchedUsers} usuarios sin coincidencia` : ''}${skippedCells ? `, ${skippedCells} celdas omitidas` : ''}.`;

      if (workingSchedule?.id) {
        const nextSchedule = {
          ...workingSchedule,
          assignments: importedAssignments,
          status: workingSchedule.status === 'published' ? 'draft' : workingSchedule.status,
        };

        setWorkingSchedule(nextSchedule);
        await persistSchedule('draft', { scheduleOverride: nextSchedule });
        setStatusFeedback(summary);
        addNotification(
          'Horarios',
          `Importación aplicada: ${summary}`,
          skippedCells || unmatchedUsers ? 'warning' : 'success'
        );

        if (sampleSkipped.length) {
          setStatusFeedback(sampleSkipped.join(' | '));
        }
        return;
      }

      const firstDay = importedDays[0];
      const lastDay = importedDays[importedDays.length - 1];
      const title = createScheduleTitle(firstDay.date, lastDay.date);

      const response = await axios.post(`${API_URL}/api/schedules`, {
        title,
        startDate: firstDay.date,
        endDate: lastDay.date,
        status: 'draft',
        assignments: importedAssignments,
        notes: '',
        createdBy: currentUser?.id || '',
        createdByName: currentUser?.name || '',
      });

      const nextSchedules = await fetchSchedules();
      setSchedules(nextSchedules);
      setSelectedScheduleId(response.data.id);
      setWorkingSchedule(deepClone(response.data));
      setStatusFeedback(summary);
      addNotification(
        'Horarios',
        `Quincena creada desde Excel. ${summary}`,
        skippedCells || unmatchedUsers ? 'warning' : 'success'
      );
    } catch (error) {
      console.error('Error importing schedule from Excel:', error);
      setStatusFeedback(error?.message || 'No se pudo importar el archivo Excel.');
      addNotification(
        'Horarios',
        error?.message || 'No se pudo importar el archivo Excel.',
        'error'
      );
    } finally {
      setImporting(false);
      if (importInputRef.current) {
        importInputRef.current.value = '';
      }
    }
  };

  const handleImportFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await processImportedWorkbook(file);
  };

  const openScheduleModal = (mode) => {
    const base = getDefaultSchedulePayload(currentUser);

    if (mode === 'duplicate' && workingSchedule) {
      const startDate = addDays(workingSchedule.endDate, 1);
      const endDate = getFortnightEndDate(startDate);
      setScheduleModal({
        open: true,
        mode,
        baseId: workingSchedule.id,
        title: `${workingSchedule.title} (copia)`,
        startDate,
        endDate,
      });
      return;
    }

    setScheduleModal({
      open: true,
      title: base.title,
      startDate: base.startDate,
      endDate: base.endDate,
      mode,
      baseId: '',
    });
  };

  const closeScheduleModal = () => {
    setScheduleModal({
      open: false,
      title: '',
      startDate: '',
      endDate: '',
      mode: 'create',
      baseId: '',
    });
  };

  const handleScheduleStartDateChange = (startDate) => {
    const normalizedStart = normalizeDateForStorage(startDate);
    const endDate = getFortnightEndDate(normalizedStart);
    setScheduleModal((prev) => ({
      ...prev,
      startDate: normalizedStart,
      endDate,
      title: !prev.title?.trim() || prev.title === createScheduleTitle(prev.startDate, prev.endDate)
        ? createScheduleTitle(normalizedStart, endDate)
        : prev.title,
    }));
  };

  const handleScheduleEndDateChange = (endDate) => {
    const normalizedEnd = normalizeDateForStorage(endDate);
    setScheduleModal((prev) => ({
      ...prev,
      endDate: normalizedEnd,
      title: !prev.title?.trim() || prev.title === createScheduleTitle(prev.startDate, prev.endDate)
        ? createScheduleTitle(prev.startDate, normalizedEnd)
        : prev.title,
    }));
  };

  const submitScheduleModal = async () => {
    if (!scheduleModal.startDate) {
      addNotification('Horarios', 'Debes seleccionar la fecha inicial de la quincena.', 'warning');
      return;
    }

    if (!scheduleModal.endDate) {
      addNotification('Horarios', 'Debes seleccionar la fecha final de la quincena.', 'warning');
      return;
    }

    const start = parseFlexibleDate(scheduleModal.startDate);
    const end = parseFlexibleDate(scheduleModal.endDate);
    if (!start || !end || end < start) {
      addNotification('Horarios', 'La fecha final debe ser igual o posterior a la fecha inicial.', 'warning');
      return;
    }

    const title = scheduleModal.title?.trim() || createScheduleTitle(scheduleModal.startDate, scheduleModal.endDate);

    try {
      setSaving(true);

      if (scheduleModal.mode === 'duplicate' && scheduleModal.baseId) {
        const response = await axios.post(`${API_URL}/api/schedules/${scheduleModal.baseId}/duplicate`, {
          title,
          startDate: scheduleModal.startDate,
          endDate: scheduleModal.endDate,
          createdBy: currentUser?.id || '',
          createdByName: currentUser?.name || '',
        });

        const nextSchedules = await fetchSchedules();
        setSelectedScheduleId(response.data.id);
        setWorkingSchedule(deepClone(response.data));
        setSchedules(nextSchedules);
        addNotification('Horarios', 'Quincena duplicada correctamente.', 'success');
      } else {
        const response = await axios.post(`${API_URL}/api/schedules`, {
          title,
          startDate: scheduleModal.startDate,
          endDate: scheduleModal.endDate,
          status: 'draft',
          assignments: [],
          notes: '',
          createdBy: currentUser?.id || '',
          createdByName: currentUser?.name || '',
        });

        const nextSchedules = await fetchSchedules();
        setSelectedScheduleId(response.data.id);
        setWorkingSchedule(deepClone(response.data));
        setSchedules(nextSchedules);
        addNotification('Horarios', 'Quincena creada correctamente.', 'success');
      }

      closeScheduleModal();
    } catch (error) {
      console.error('Error saving schedule:', error);
      addNotification('Horarios', 'No se pudo crear la quincena.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const persistSchedule = async (nextStatus = 'draft', options = {}) => {
    const {
      skipPublishConfirmation = false,
      scheduleOverride = null,
    } = options;
    const scheduleToPersist = scheduleOverride || workingSchedule;
    if (!scheduleToPersist?.id) return;

    if (nextStatus === 'published' && !skipPublishConfirmation) {
      const otherPublished = schedules.find(
        (schedule) => schedule.id !== scheduleToPersist.id && schedule.status === 'published'
      );

      if (otherPublished) {
        setPublishConfirm({
          open: true,
          targetStatus: nextStatus,
          conflictingSchedule: otherPublished,
        });
        return;
      }
    }

    try {
      setSaving(true);
      const response = nextStatus === 'published'
        ? await axios.post(`${API_URL}/api/schedules/${scheduleToPersist.id}/publish`, {
            updatedBy: currentUser?.id || '',
            updatedByName: currentUser?.name || '',
            summary: 'Horario publicado',
          })
        : await axios.put(`${API_URL}/api/schedules/${scheduleToPersist.id}`, {
            title: scheduleToPersist.title,
            startDate: scheduleToPersist.startDate,
            endDate: scheduleToPersist.endDate,
            status: nextStatus,
            assignments: scheduleToPersist.assignments || [],
            notes: scheduleToPersist.notes || '',
            updatedBy: currentUser?.id || '',
            updatedByName: currentUser?.name || '',
          });

      const nextSchedules = await fetchSchedules();
      setSchedules(nextSchedules);
      setWorkingSchedule(deepClone(response.data));
      setSelectedScheduleId(response.data.id);
      setStatusFeedback(
        nextStatus === 'published'
          ? 'Horario publicado correctamente.'
          : 'Borrador guardado correctamente.'
      );

      addNotification(
        'Horarios',
        nextStatus === 'published' ? 'Horario publicado correctamente.' : 'Borrador guardado correctamente.',
        'success'
      );
    } catch (error) {
      console.error('Error persisting schedule:', error);
      addNotification('Horarios', 'No se pudo guardar el horario.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSchedule = async (schedule) => {
    if (!schedule?.id) return;

    try {
      setSaving(true);
      await axios.delete(`${API_URL}/api/schedules/${schedule.id}`);

      const nextSchedules = await fetchSchedules();
      setSchedules(nextSchedules);

      if (selectedScheduleId === schedule.id) {
        const remainingPreferred = getPreferredSchedule(nextSchedules, currentUser, isManager);
        setSelectedScheduleId(remainingPreferred?.id || '');
        setWorkingSchedule(remainingPreferred ? deepClone(remainingPreferred) : null);
      }

      addNotification('Horarios', 'Quincena eliminada correctamente.', 'success');
    } catch (error) {
      console.error('Error deleting schedule:', error);
      addNotification('Horarios', 'No se pudo eliminar la quincena.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const openAssignmentEditor = (user, day) => {
    const existing = assignmentMap[buildAssignmentKey(user.id, day.date)];
    const definition = getDefinition(existing?.code || 'day_5_7');

    setEditor({
      open: true,
      user,
      day,
      code: existing?.code || definition.code,
      startTime: existing?.startTime || definition.startTime || '',
      endTime: existing?.endTime || definition.endTime || '',
      notes: existing?.notes || '',
    });
  };

  const closeAssignmentEditor = () => {
    setEditor({
      open: false,
      user: null,
      day: null,
      code: 'day_5_7',
      startTime: shiftLibrary.day_5_7?.startTime || DEFAULT_SHIFT_LIBRARY.day_5_7.startTime,
      endTime: shiftLibrary.day_5_7?.endTime || DEFAULT_SHIFT_LIBRARY.day_5_7.endTime,
      notes: '',
    });
  };

  const validateAssignment = (user, day, code, startTime, endTime) => {
    const settings = normalizeScheduleSettings(user?.scheduleSettings, availableShiftCodes);
    const definition = getDefinition(code);
    const requiresTimeRange = definition.category === 'shift' || code === 'permission';

    if (code !== 'unassigned' && !availableShiftCodes.includes(code)) {
      return 'Ese turno o estado no está habilitado en la configuración global.';
    }

    if (code !== 'unassigned' && !settings.allowedShiftCodes.includes(code)) {
      return 'Ese turno o estado no está permitido para el usuario.';
    }

    if (code === 'night_6_6' && !settings.canNightShift) {
      return 'El usuario no puede hacer turnos nocturnos.';
    }

    if (code === 'day_7_9' && !settings.canNineToNine) {
      return 'El usuario no puede hacer el turno extendido de 7 a 9.';
    }

    if (settings.dayOnly && definition.code === 'night_6_6') {
      return 'El usuario solo puede trabajar en jornada diurna.';
    }

    if (day?.isSunday && !settings.canWorkSunday && !isOffCode(code)) {
      return 'El usuario no puede ser asignado a trabajar en domingo.';
    }

    if (requiresTimeRange && (!startTime || !endTime)) {
      return 'Debes indicar hora inicial y hora final.';
    }

    return '';
  };

  const saveAssignmentLocally = async () => {
    if (!editor.user || !editor.day || !workingSchedule) return;

    const validationError = validateAssignment(
      editor.user,
      editor.day,
      editor.code,
      editor.startTime,
      editor.endTime
    );
    if (validationError) {
      addNotification('Horarios', validationError, 'warning');
      return;
    }

    const definition = getDefinition(editor.code);
    const assignmentKey = buildAssignmentKey(editor.user.id, editor.day.date);
    const nextAssignments = (workingSchedule.assignments || []).filter(
      (assignment) => buildAssignmentKey(assignment.userId, assignment.date) !== assignmentKey
    );

    if (editor.code !== 'unassigned') {
      const shouldPersistTimeRange = definition.category === 'shift' || definition.code === 'permission';
      const assignmentTimeLabel = shouldPersistTimeRange
        ? formatScheduleTimeRange(editor.startTime, editor.endTime)
        : '';
      nextAssignments.push({
        userId: editor.user.id,
        date: editor.day.date,
        code: editor.code,
        label: assignmentTimeLabel || definition.label,
        category: definition.category,
        startTime: shouldPersistTimeRange ? editor.startTime : '',
        endTime: shouldPersistTimeRange ? editor.endTime : '',
        color: definition.color,
        notes: editor.notes || '',
      });
    }

    const nextSchedule = {
      ...workingSchedule,
      assignments: nextAssignments,
      status: workingSchedule.status === 'published' ? 'draft' : workingSchedule.status,
    };
    setWorkingSchedule(nextSchedule);
    closeAssignmentEditor();
    await persistSchedule('draft', { scheduleOverride: nextSchedule });
  };

  const openSettingsModal = (user) => {
    setSettingsModal({
      open: true,
      user,
      settings: normalizeScheduleSettings(user.scheduleSettings, availableShiftCodes),
    });
  };

  const closeSettingsModal = () => {
    setSettingsModal({
      open: false,
      user: null,
      settings: normalizeScheduleSettings({}, availableShiftCodes),
    });
  };

  const openGlobalSettingsModal = () => {
    setGlobalSettingsModal({
      open: true,
      shiftOptions: normalizeShiftOptions(globalConfig.shiftOptions),
    });
  };

  const closeGlobalSettingsModal = () => {
    setGlobalSettingsModal({
      open: false,
      shiftOptions: normalizeShiftOptions(globalConfig.shiftOptions),
    });
  };

  const saveGlobalScheduleSettings = async () => {
    try {
      setSaving(true);
      const payload = {
        shiftOptions: normalizeShiftOptions(globalSettingsModal.shiftOptions),
        updatedBy: currentUser?.id || '',
        updatedByName: currentUser?.name || '',
      };
      const response = await axios.put(`${API_URL}/api/schedule-config`, payload);
      const normalized = normalizeShiftOptions(response.data?.shiftOptions);

      setGlobalConfig({
        shiftOptions: normalized,
        updatedAt: response.data?.updatedAt || '',
        updatedBy: response.data?.updatedBy || '',
        updatedByName: response.data?.updatedByName || '',
      });
      addNotification('Horarios', 'Configuración global actualizada.', 'success');
    } catch (error) {
      console.error('Error saving global schedule settings:', error);
      addNotification('Horarios', 'No se pudo guardar la configuración global.', 'error');
    } finally {
      closeGlobalSettingsModal();
      setSaving(false);
    }
  };

  const saveUserScheduleSettings = async () => {
    if (!settingsModal.user) return;
    const user = settingsModal.user;

    try {
      setSaving(true);
      await axios.put(`${API_URL}/api/users/${user.id}`, {
        name: user.name,
        email: user.email,
        role: user.role,
        avatar: user.avatar,
        aliases: user.aliases || [],
        sourceMappings: user.sourceMappings || {},
        insurerPercentages: user.insurerPercentages || {},
        permissions: user.permissions || [],
        mustChangePassword: !!user.mustChangePassword,
        scheduleSettings: {
          ...settingsModal.settings,
          allowedShiftCodes: settingsModal.settings.allowedShiftCodes.filter((code) => availableShiftCodes.includes(code)),
        },
      });

      await fetchUsers();
      addNotification('Horarios', 'Configuración del usuario actualizada.', 'success');
    } catch (error) {
      console.error('Error saving user schedule settings:', error);
      addNotification('Horarios', 'No se pudo guardar la configuración del usuario.', 'error');
    } finally {
      closeSettingsModal();
      setSaving(false);
    }
  };

  const buildExportRows = () => {
    const rows = [];
    const header = ['Usuario', ...currentScheduleDays.map((day) => `${day.dayName} ${day.label}`)];
    rows.push(header);

    visibleUsers.forEach((user) => {
      const row = [user.name];
      currentScheduleDays.forEach((day) => {
        const assignment = assignmentMap[buildAssignmentKey(user.id, day.date)];
        const definition = getDefinition(assignment?.code);
        row.push(getAssignmentDisplayText(assignment, definition));
      });
      rows.push(row);
    });

    return rows;
  };

  const buildTemplateDays = () => {
    if (currentScheduleDays.length) {
      return currentScheduleDays;
    }

    const base = getDefaultSchedulePayload(currentUser);
    return buildFortnightDays(base.startDate, base.endDate);
  };

  const buildTemplateRows = () => {
    const days = buildTemplateDays();
    const rows = [];
    const header = ['ID', 'Usuario', ...days.map((day) => `${day.dayName} ${day.label}`)];
    rows.push(header);

    importableUsers.forEach((user) => {
      rows.push([user.id || '', user.name, ...days.map(() => '')]);
    });

    return rows;
  };

  const buildTemplateUserMasterRows = () => {
    const rows = [[
      'ID',
      'Nombre',
      'Email',
      'Rol',
      'Dias libre preferido',
      'Puede nocturno',
      'Puede 9 a 9',
      'Puede domingo',
      'Solo diurno',
      'Turnos permitidos',
    ]];

    importableUsers.forEach((user) => {
      const settings = normalizeScheduleSettings(user.scheduleSettings, availableShiftCodes);
      rows.push([
        user.id || '',
        user.name || '',
        user.email || '',
        getRoleLabel(user.role),
        settings.preferredDayOff || '',
        settings.canNightShift ? 'Si' : 'No',
        settings.canNineToNine ? 'Si' : 'No',
        settings.canWorkSunday ? 'Si' : 'No',
        settings.dayOnly ? 'Si' : 'No',
        (settings.allowedShiftCodes || []).join(', '),
      ]);
    });

    return rows;
  };

  const buildTemplateShiftMasterRows = () => {
    const rows = [[
      'Codigo',
      'Etiqueta',
      'Etiqueta corta',
      'Categoria',
      'Hora inicial',
      'Hora final',
      'Activo',
    ]];

    globalConfig.shiftOptions.forEach((option) => {
      rows.push([
        option.code || '',
        option.label || '',
        option.shortLabel || '',
        option.category || '',
        option.startTime || '',
        option.endTime || '',
        option.enabled === false ? 'No' : 'Si',
      ]);
    });

    return rows;
  };

  const buildTemplateInstructionsRows = () => {
    const days = buildTemplateDays();
    return [
      ['Instruccion', 'Valor'],
      ['Uso', 'Llena solo las celdas del horario. No cambies los nombres de la columna Usuario ni el orden de las fechas.'],
      ['Usuarios', 'La hoja principal ya trae los usuarios activos asignables.'],
      ['Turnos', 'Puedes usar codigos, etiquetas o rangos horarios.'],
      ['Fechas', `La plantilla esta preparada para ${days.length} dias.`],
      ['Importacion', 'La primera hoja se usa al importar. Las hojas Maestro y Usuarios son de referencia.'],
    ];
  };

  const exportToExcel = () => {
    if (!workingSchedule) return;

    const worksheet = XLSX.utils.aoa_to_sheet(buildExportRows());
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Horarios');
    XLSX.writeFile(workbook, `${workingSchedule.title}.xlsx`);
  };

  const exportTemplateToExcel = () => {
    const workbook = XLSX.utils.book_new();
    const templateSheet = XLSX.utils.aoa_to_sheet(buildTemplateRows());
    const userMasterSheet = XLSX.utils.aoa_to_sheet(buildTemplateUserMasterRows());
    const shiftMasterSheet = XLSX.utils.aoa_to_sheet(buildTemplateShiftMasterRows());
    const instructionsSheet = XLSX.utils.aoa_to_sheet(buildTemplateInstructionsRows());

    XLSX.utils.book_append_sheet(workbook, templateSheet, 'Plantilla');
    XLSX.utils.book_append_sheet(workbook, userMasterSheet, 'Usuarios');
    XLSX.utils.book_append_sheet(workbook, shiftMasterSheet, 'Maestro');
    XLSX.utils.book_append_sheet(workbook, instructionsSheet, 'Instrucciones');

    const fileBase = workingSchedule?.title ? `${workingSchedule.title} - Plantilla` : 'Plantilla Horarios';
    XLSX.writeFile(workbook, `${fileBase}.xlsx`);
  };

  const buildPrintableHtml = () => {
    if (!workingSchedule) return '';

    const rows = visibleUsers.map((user) => {
      const cells = currentScheduleDays
        .map((day) => {
          const assignment = assignmentMap[buildAssignmentKey(user.id, day.date)];
          const definition = getDefinition(assignment?.code);
          const label = getAssignmentDisplayText(assignment, definition);
          return `<td>${label}</td>`;
        })
        .join('');

      return `<tr><td>${user.name}</td>${cells}</tr>`;
    }).join('');

    const headerCells = currentScheduleDays
      .map((day) => `<th>${day.dayName}<br/>${day.label}</th>`)
      .join('');

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${workingSchedule.title}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #0f172a; }
            h1 { margin-bottom: 8px; }
            p { margin-top: 0; color: #475569; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; }
            th, td { border: 1px solid #cbd5e1; padding: 6px; text-align: center; }
            th:first-child, td:first-child { text-align: left; position: sticky; left: 0; background: #fff; }
          </style>
        </head>
        <body>
          <h1>${workingSchedule.title}</h1>
          <p>${formatDateDisplay(workingSchedule.startDate)} al ${formatDateDisplay(workingSchedule.endDate)}</p>
          <table>
            <thead>
              <tr><th>Usuario</th>${headerCells}</tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </body>
      </html>
    `;
  };

  const exportToPdf = () => {
    const printable = buildPrintableHtml();
    if (!printable) return;

    const printWindow = window.open('', '_blank', 'width=1280,height=900');
    if (!printWindow) {
      addNotification('Horarios', 'Tu navegador bloqueó la ventana de impresión.', 'warning');
      return;
    }

    printWindow.document.write(printable);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 300);
  };

  const exportToImage = () => {
    if (!workingSchedule) return;

    const cellWidth = 110;
    const rowHeight = 42;
    const titleHeight = 70;
    const width = 240 + currentScheduleDays.length * cellWidth;
    const height = titleHeight + (visibleUsers.length + 1) * rowHeight;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    canvas.width = width;
    canvas.height = height;

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.fillStyle = '#0f172a';
    context.font = 'bold 24px Arial';
    context.fillText(workingSchedule.title, 24, 34);
    context.font = '14px Arial';
    context.fillStyle = '#475569';
    context.fillText(`${formatDateDisplay(workingSchedule.startDate)} al ${formatDateDisplay(workingSchedule.endDate)}`, 24, 58);

    context.font = 'bold 12px Arial';
    context.fillStyle = '#e2e8f0';
    context.fillRect(0, titleHeight, width, rowHeight);
    context.fillStyle = '#0f172a';
    context.fillText('Usuario', 16, titleHeight + 24);

    currentScheduleDays.forEach((day, index) => {
      const x = 240 + index * cellWidth;
      context.fillText(`${day.dayName} ${day.label}`, x + 8, titleHeight + 24);
    });

    visibleUsers.forEach((user, rowIndex) => {
      const y = titleHeight + (rowIndex + 1) * rowHeight;
      context.strokeStyle = '#cbd5e1';
      context.strokeRect(0, y, width, rowHeight);
      context.fillStyle = '#0f172a';
      context.font = '12px Arial';
      context.fillText(user.name, 16, y + 24);

      currentScheduleDays.forEach((day, columnIndex) => {
        const assignment = assignmentMap[buildAssignmentKey(user.id, day.date)];
        const definition = getDefinition(assignment?.code);
        const x = 240 + columnIndex * cellWidth;
        context.fillStyle = assignment ? definition.color : '#f8fafc';
        context.fillRect(x, y, cellWidth, rowHeight);
        context.strokeStyle = '#cbd5e1';
        context.strokeRect(x, y, cellWidth, rowHeight);
        context.fillStyle = assignment ? '#ffffff' : '#64748b';
        context.fillText(getAssignmentDisplayText(assignment, definition), x + 8, y + 24);
      });
    });

    const link = document.createElement('a');
    link.download = `${workingSchedule.title}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!workingSchedule && canManage) {
    return (
      <div className="space-y-6 p-4 md:p-6">
        <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="mx-auto max-w-2xl space-y-5 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
              <CalendarDays className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Horarios</h1>
              <p className="mt-2 text-sm text-slate-500">
                Crea tu primera quincena para comenzar a asignar turnos reales al personal.
              </p>
            </div>
            <button
              onClick={() => openScheduleModal('create')}
              className="inline-flex items-center gap-2 rounded-2xl bg-primary px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-primary/30 transition hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" />
              Nueva quincena
            </button>
            <button
              onClick={exportTemplateToExcel}
              className="inline-flex items-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-5 py-3 text-sm font-semibold text-blue-700 transition hover:bg-blue-100"
            >
              <FileSpreadsheet className="h-4 w-4" />
              Plantilla Excel
            </button>
            <button
              onClick={triggerImportSchedule}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <FileUp className="h-4 w-4" />
              Importar Excel
            </button>
          </div>
        </div>

        {scheduleModal.open && (
          <ScheduleModal
            modal={scheduleModal}
            onClose={closeScheduleModal}
            onChange={setScheduleModal}
            onStartDateChange={handleScheduleStartDateChange}
            onEndDateChange={handleScheduleEndDateChange}
            onSubmit={submitScheduleModal}
            saving={saving}
          />
        )}
      </div>
    );
  }

  if (!workingSchedule && !canManage) {
    return (
      <div className="space-y-6 p-4 md:p-6">
        <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="mx-auto max-w-2xl space-y-3 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-600">
              <ClipboardList className="h-8 w-8" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Horarios</h1>
            <p className="text-sm text-slate-500">
              Aún no hay una quincena publicada para consultar tu horario.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-3 md:p-6 ${canManage ? 'flex h-full min-h-0 flex-col gap-5 overflow-hidden' : 'space-y-5'}`}>
      <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm md:p-4">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
                <CalendarDays className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-xl font-bold text-slate-900">Horarios</h1>
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                    workingSchedule.status === 'published'
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-amber-100 text-amber-800'
                  }`}>
                    {workingSchedule.status === 'published' ? 'Publicado' : 'Borrador'}
                  </span>
                </div>
                <p className="truncate text-sm text-slate-500">
                  {workingSchedule.title}
                </p>
              </div>
            </div>

            {canManage && (
              <div className="flex flex-wrap gap-2 xl:justify-end">
                <select
                  value={selectedScheduleId}
                  onChange={(event) => setSelectedScheduleId(event.target.value)}
                  className="min-w-[260px] rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                >
                  {schedules.map((schedule) => (
                    <option key={schedule.id} value={schedule.id}>
                      {schedule.title} · {schedule.status === 'published' ? 'Publicado' : 'Borrador'}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => openScheduleModal('create')}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
                >
                  <Plus className="h-4 w-4" />
                  Nueva
                </button>
                <button
                  onClick={() => openScheduleModal('duplicate')}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
                >
                  <Copy className="h-4 w-4" />
                  Duplicar
                </button>
                <button
                  onClick={exportTemplateToExcel}
                  className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 shadow-sm transition hover:bg-blue-100"
                >
                  <FileSpreadsheet className="h-4 w-4" />
                  Plantilla
                </button>
                <button
                  onClick={triggerImportSchedule}
                  disabled={importing}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FileUp className="h-4 w-4" />
                  {importing ? 'Importando...' : 'Importar'}
                </button>
                <button
                  onClick={() => setDeleteConfirm({ open: true, schedule: workingSchedule })}
                  disabled={saving || !workingSchedule}
                  className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-600 shadow-sm transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <X className="h-4 w-4" />
                  Borrar
                </button>
                <button
                  onClick={() => persistSchedule('draft')}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-3 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
                >
                  <Save className="h-4 w-4" />
                  Borrador
                </button>
                <button
                  onClick={openGlobalSettingsModal}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
                >
                  <Settings2 className="h-4 w-4" />
                  Configuración global
                </button>
                <button
                  onClick={() => persistSchedule('published')}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-xl bg-primary px-3 py-2 text-sm font-medium text-white shadow-lg shadow-primary/20 transition hover:bg-primary/90 disabled:opacity-60"
                >
                  <Check className="h-4 w-4" />
                  Publicar
                </button>
                <button
                  onClick={() => setHeaderExtrasVisible((prev) => !prev)}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-800"
                  title={headerExtrasVisible ? 'Ocultar opciones' : 'Mostrar opciones'}
                >
                  <ChevronDown className={`h-4 w-4 transition-transform ${headerExtrasVisible ? 'rotate-180' : ''}`} />
                </button>
              </div>
            )}
          </div>

          {statusFeedback && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700">
              {statusFeedback}
            </div>
          )}
        </div>

        {headerExtrasVisible && (
          <>
            <div className="mt-3 flex flex-wrap gap-2">
              {canManage && (
                <>
                  <button
                    onClick={exportToExcel}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                    Excel
                  </button>
                  <button
                    onClick={exportTemplateToExcel}
                    className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 transition hover:bg-blue-100"
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                    Plantilla Excel
                  </button>
                  <button
                    onClick={exportToPdf}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    <FileText className="h-4 w-4" />
                    PDF
                  </button>
                  <button
                    onClick={exportToImage}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    <FileImage className="h-4 w-4" />
                    Imagen
                  </button>
                </>
              )}
              <button
                onClick={() => setHistoryOpen(true)}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <History className="h-4 w-4" />
                Historial
              </button>
              {canManage && (
                <button
                  onClick={fetchSchedules}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  <RefreshCw className="h-4 w-4" />
                  Refrescar
                </button>
              )}
            </div>

            <div className="mt-3">
              <button
                onClick={() => setLegendExpanded((prev) => !prev)}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 hover:text-slate-800"
              >
                <ChevronDown className={`h-4 w-4 transition-transform ${legendExpanded ? 'rotate-180' : ''}`} />
                {legendExpanded ? 'Ocultar leyenda' : 'Mostrar leyenda'}
              </button>

              {legendExpanded && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {enabledShiftOptions.map((option) => (
                    <span
                      key={option.code}
                      className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${option.bgClass}`}
                    >
                      {option.shortLabel}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {canManage ? (
        <ManagerScheduleView
          users={visibleUsers}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          days={currentScheduleDays}
          assignmentMap={assignmentMap}
          getDefinition={getDefinition}
          onEditCell={openAssignmentEditor}
          onEditSettings={openSettingsModal}
        />
      ) : (
        <CollaboratorScheduleView
          currentUser={currentUser}
          workingSchedule={workingSchedule}
          currentScheduleDays={currentScheduleDays}
          ownAssignments={ownAssignments}
          ownScheduleSummary={ownScheduleSummary}
          currentWeekAssignments={currentWeekAssignments}
          getDefinition={getDefinition}
        />
      )}

      {scheduleModal.open && (
        <ScheduleModal
          modal={scheduleModal}
          onClose={closeScheduleModal}
          onChange={setScheduleModal}
          onStartDateChange={handleScheduleStartDateChange}
          onEndDateChange={handleScheduleEndDateChange}
          onSubmit={submitScheduleModal}
          saving={saving}
        />
      )}

      {publishConfirm.open && (
        <PublishConfirmModal
          conflictingSchedule={publishConfirm.conflictingSchedule}
          onClose={() => setPublishConfirm({ open: false, targetStatus: '', conflictingSchedule: null })}
          onConfirm={() => {
            setPublishConfirm({ open: false, targetStatus: '', conflictingSchedule: null });
            persistSchedule('published', { skipPublishConfirmation: true });
          }}
        />
      )}

      <ConfirmDialog
        open={deleteConfirm.open}
        title="Borrar quincena"
        message={
          deleteConfirm.schedule
            ? `Se eliminará la quincena "${deleteConfirm.schedule.title}". Esta acción no se puede deshacer.`
            : 'Se eliminará la quincena seleccionada. Esta acción no se puede deshacer.'
        }
        confirmLabel="Borrar"
        confirmClassName="bg-red-600 hover:bg-red-700"
        onCancel={() => setDeleteConfirm({ open: false, schedule: null })}
        onConfirm={() => {
          const scheduleToDelete = deleteConfirm.schedule;
          setDeleteConfirm({ open: false, schedule: null });
          handleDeleteSchedule(scheduleToDelete);
        }}
      />

      {editor.open && (
        <AssignmentEditorModal
          editor={editor}
          shiftOptions={enabledShiftOptions}
          getDefinition={getDefinition}
          setEditor={setEditor}
          onClose={closeAssignmentEditor}
          onSave={saveAssignmentLocally}
        />
      )}

      {settingsModal.open && (
        <SettingsModal
          modal={settingsModal}
          shiftOptions={enabledShiftOptions}
          setModal={setSettingsModal}
          onClose={closeSettingsModal}
          onSave={saveUserScheduleSettings}
          saving={saving}
        />
      )}

      {globalSettingsModal.open && (
        <GlobalScheduleConfigModal
          modal={globalSettingsModal}
          setModal={setGlobalSettingsModal}
          onClose={closeGlobalSettingsModal}
          onSave={saveGlobalScheduleSettings}
          saving={saving}
        />
      )}

      {historyOpen && (
        <HistoryModal
          schedule={workingSchedule}
          onClose={() => setHistoryOpen(false)}
        />
      )}

      <input
        ref={importInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        className="hidden"
        onChange={handleImportFileChange}
      />
    </div>
  );
};

const ManagerScheduleView = ({
  users,
  searchTerm,
  setSearchTerm,
  days,
  assignmentMap,
  getDefinition,
  onEditCell,
  onEditSettings,
}) => (
  <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
    <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Planificación quincenal</h2>
        <p className="text-sm text-slate-500">
          Haz clic en cualquier celda para asignar o cambiar un turno.
        </p>
      </div>
      <div className="relative w-full md:max-w-sm">
        <UserRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Buscar usuario..."
          className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-700 shadow-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
      </div>
    </div>

    <div className="relative isolate min-h-0 flex-1 overflow-auto">
      <div className="min-w-[1200px]">
        <div
          className="sticky top-0 z-30 grid border-b border-slate-200 bg-slate-50"
          style={{ gridTemplateColumns: `240px repeat(${days.length}, minmax(90px, 1fr))` }}
        >
          <div className="sticky left-0 z-20 border-r border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700">
            Usuario
          </div>
          {days.map((day) => (
            <div
              key={day.date}
              className={`relative z-10 border-r border-slate-200 px-2 py-3 text-center text-xs font-semibold ${
                day.isSunday ? 'bg-rose-50 text-rose-700' : 'bg-slate-50 text-slate-600'
              }`}
            >
              <div>{day.dayName}</div>
              <div className="mt-1 text-[11px] font-medium">{day.label}</div>
            </div>
          ))}
        </div>

        {users.map((user) => (
          <div
            key={user.id}
            className="grid border-b border-slate-100"
            style={{ gridTemplateColumns: `240px repeat(${days.length}, minmax(90px, 1fr))` }}
          >
            <div className="sticky left-0 z-10 flex items-center justify-between gap-3 border-r border-slate-200 bg-white px-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-900">{user.name}</p>
                <p className="truncate text-xs text-slate-500">{getRoleLabel(user.role)}</p>
              </div>
              <button
                onClick={() => onEditSettings(user)}
                className="rounded-xl border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700"
                title="Configurar restricciones"
              >
                <Settings2 className="h-4 w-4" />
              </button>
            </div>

            {days.map((day) => {
              const assignment = assignmentMap[buildAssignmentKey(user.id, day.date)];
              const definition = getDefinition(assignment?.code);
              return (
                <button
                  key={buildAssignmentKey(user.id, day.date)}
                  onClick={() => onEditCell(user, day)}
                  className={`m-1 flex min-h-[76px] flex-col justify-between overflow-hidden rounded-2xl border px-2 py-2 text-left transition hover:z-10 hover:scale-[1.02] ${assignment ? definition.bgClass : 'border-dashed border-slate-200 bg-white text-slate-400 hover:border-primary/50'}`}
                >
                  {assignment && (
                    <>
                      <span className="text-[11px] font-semibold">
                        {definition.shortLabel}
                      </span>
                      <span className="text-[10px] leading-4">
                        {assignment.startTime && assignment.endTime
                          ? formatScheduleTimeRange(assignment.startTime, assignment.endTime)
                          : definition.label}
                      </span>
                    </>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  </div>
);

const CollaboratorScheduleView = ({
  currentUser,
  workingSchedule,
  currentScheduleDays,
  ownAssignments,
  ownScheduleSummary,
  currentWeekAssignments,
  getDefinition,
}) => {
  const assignmentMap = getAssignmentByKey(ownAssignments);
  const thisFortnight = currentScheduleDays.map((day) => ({
    ...day,
    assignment: assignmentMap[buildAssignmentKey(currentUser.id, day.date)] || null,
  }));

  return (
    <div className="space-y-3 md:space-y-5">
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <SummaryCard
          title="Mi horario de hoy"
          value={ownScheduleSummary.todayAssignment
            ? getAssignmentDisplayText(
                ownScheduleSummary.todayAssignment,
                getDefinition(ownScheduleSummary.todayAssignment.code)
              )
            : 'Sin asignar'}
          subtitle={ownScheduleSummary.todayAssignment
            ? getAssignmentSecondaryText(
                ownScheduleSummary.todayAssignment,
                getDefinition(ownScheduleSummary.todayAssignment.code)
              )
            : 'No hay turno cargado para hoy'}
          icon={Sun}
          tone="blue"
          compact
        />
        <SummaryCard
          title="Mi semana"
          value={`${currentWeekAssignments.length} días`}
          subtitle="Asignaciones visibles esta semana"
          icon={CalendarDays}
          tone="green"
          compact
        />
        <SummaryCard
          title="Mis días libres"
          value={String(ownScheduleSummary.freeDays.length)}
          subtitle="Libre y descanso compensatorio"
          icon={Moon}
          tone="slate"
          compact
        />
        <SummaryCard
          title="Mis nocturnos"
          value={String(ownScheduleSummary.nightAssignments.length)}
          subtitle="Turnos nocturnos en la quincena"
          icon={Moon}
          tone="violet"
          compact
        />
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm md:rounded-3xl">
        <div className="border-b border-slate-200 bg-slate-50/70 px-3 py-3 md:px-6 md:py-4">
          <h2 className="text-lg font-semibold text-slate-900">Mi quincena</h2>
          <p className="text-xs text-slate-500 md:text-sm">{toRenderableText(workingSchedule.title)}</p>
        </div>

        <div className="p-2.5 md:p-4">
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-5">
            {thisFortnight.map((day) => {
              const definition = getDefinition(day.assignment?.code);
              const primaryText = day.assignment
                ? toRenderableText(getAssignmentDisplayText(day.assignment, definition))
                : 'Día no asignado';
              const secondaryText = (() => {
                if (!day.assignment) return 'Sin turno';

                if (definition.category === 'shift' || definition.code === 'permission') {
                  const shiftLabel = toRenderableText(definition.shortLabel || definition.label);
                  return shiftLabel !== primaryText ? shiftLabel : '';
                }

                const note = toRenderableText(day.assignment.notes || '');
                if (note && note !== primaryText) return note;

                const stateLabel = toRenderableText(definition.label);
                return stateLabel !== primaryText ? stateLabel : '';
              })();
              return (
                <div
                  key={day.date}
                  className={`rounded-xl border px-2 py-2 transition md:px-2.5 ${
                    day.assignment
                      ? `${definition.bgClass} shadow-[0_8px_24px_-20px_rgba(15,23,42,0.45)]`
                      : 'border-slate-200 bg-white text-slate-500'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[9px] font-semibold uppercase tracking-wide md:text-[10px]">{toRenderableText(day.dayName)}</p>
                      <p className="text-sm font-semibold leading-5 md:text-[1rem]">{toRenderableText(day.label)}</p>
                    </div>
                    <span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${
                      day.isSunday
                        ? 'bg-rose-100 text-rose-700'
                        : day.assignment
                          ? 'bg-white/65 text-slate-700'
                          : 'bg-slate-100 text-slate-500'
                    }`}>
                      {day.isSunday ? 'Dom' : day.assignment ? 'OK' : 'Sin'}
                    </span>
                  </div>
                  <div className="mt-1.5 border-t border-slate-200/70 pt-1.5 md:mt-2 md:pt-2">
                    <p className="text-xs font-semibold leading-4 md:text-[13px] md:leading-5">
                      {primaryText}
                    </p>
                    {secondaryText && (
                      <p className="mt-0.5 text-[10px] leading-4 md:text-[11px]">
                        {secondaryText}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const toRenderableText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (typeof value === 'object') {
    if (typeof value.message === 'string') return value.message;
    if (typeof value.msg === 'string') return value.msg;
    if (typeof value.input === 'string') return value.input;
  }
  return '';
};

const SummaryCard = ({ title, value, subtitle, icon: Icon, tone = 'blue', compact = false }) => {
  const toneMap = {
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-emerald-100 text-emerald-700',
    slate: 'bg-slate-100 text-slate-700',
    violet: 'bg-violet-100 text-violet-700',
  };

  return (
    <div className={`rounded-2xl border border-slate-200 bg-white shadow-sm ${compact ? 'min-h-[94px] p-2.5 md:min-h-[108px] md:p-3.5' : 'p-5'}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className={`${compact ? 'text-[10px] md:text-xs' : 'text-sm'} font-medium text-slate-500`}>{toRenderableText(title)}</p>
          <h3 className={`${compact ? 'mt-0.5 text-[1.15rem] leading-6 md:mt-1 md:text-[1.05rem] md:leading-6' : 'mt-3 text-2xl'} font-bold text-slate-900`}>{toRenderableText(value)}</h3>
          <p className={`${compact ? 'mt-0.5 text-[9px] leading-3.5 md:mt-1 md:text-[11px]' : 'mt-2 text-xs'} text-slate-500`}>{toRenderableText(subtitle)}</p>
        </div>
        <div className={`flex ${compact ? 'h-7 w-7 md:h-8 md:w-8' : 'h-11 w-11'} items-center justify-center rounded-xl ${toneMap[tone]}`}>
          <Icon className={`${compact ? 'h-3.5 w-3.5 md:h-4 md:w-4' : 'h-5 w-5'}`} />
        </div>
      </div>
    </div>
  );
};

const ModalShell = ({ title, subtitle, onClose, children, footer }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4">
    <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl">
      <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
        <div>
          <h3 className="text-xl font-semibold text-slate-900">{title}</h3>
          {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
        </div>
        <button
          onClick={onClose}
          className="rounded-2xl border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="max-h-[70vh] overflow-y-auto px-6 py-5">{children}</div>
      <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 px-6 py-4">
        {footer}
      </div>
    </div>
  </div>
);

const ScheduleModal = ({ modal, onClose, onChange, onStartDateChange, onEndDateChange, onSubmit, saving }) => (
  <ModalShell
    title={modal.mode === 'duplicate' ? 'Duplicar quincena' : 'Nueva quincena'}
    subtitle="Define el rango de fechas para este horario."
    onClose={onClose}
    footer={(
      <>
        <button
          onClick={onClose}
          className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
        >
          Cancelar
        </button>
        <button
          onClick={onSubmit}
          disabled={saving}
          className="rounded-2xl bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90 disabled:opacity-60"
        >
          {saving ? 'Guardando...' : (modal.mode === 'duplicate' ? 'Duplicar quincena' : 'Crear quincena')}
        </button>
      </>
    )}
  >
    <div className="grid gap-4 md:grid-cols-2">
      <label className="space-y-2">
        <span className="text-sm font-medium text-slate-700">Título</span>
        <input
          value={modal.title}
          onChange={(event) => onChange((prev) => ({ ...prev, title: event.target.value }))}
          className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
      </label>
      <label className="space-y-2">
        <span className="text-sm font-medium text-slate-700">Fecha inicial</span>
        <input
          type="date"
          value={modal.startDate}
          onChange={(event) => onStartDateChange(event.target.value)}
          className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
      </label>
      <label className="space-y-2">
        <span className="text-sm font-medium text-slate-700">Fecha final</span>
        <input
          type="date"
          value={modal.endDate}
          min={modal.startDate || undefined}
          onChange={(event) => onEndDateChange(event.target.value)}
          className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
      </label>
      <p className="md:col-span-2 text-xs text-slate-500">
        La fecha final se sugiere automáticamente y puedes ajustarla según el cierre real del mes.
      </p>
    </div>
  </ModalShell>
);

const AssignmentEditorModal = ({ editor, shiftOptions, getDefinition, setEditor, onClose, onSave }) => {
  const definition = getDefinition(editor.code);
  const requiresTimeRange = definition.category === 'shift' || definition.code === 'permission';

  return (
    <ModalShell
      title="Asignar turno o estado"
      subtitle={`${editor.user?.name || ''} · ${editor.day?.label || ''}`}
      onClose={onClose}
      footer={(
        <>
          <button
            onClick={onClose}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            Cancelar
          </button>
          <button
            onClick={onSave}
            className="rounded-2xl bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90"
          >
            Aplicar
          </button>
        </>
      )}
    >
      <div className="space-y-5">
        <div className="grid gap-3 md:grid-cols-2">
          {shiftOptions.map((option) => (
            <button
              key={option.code}
              onClick={() => setEditor((prev) => ({
                ...prev,
                code: option.code,
                startTime: option.category === 'shift'
                  ? (option.startTime || '')
                  : (option.code === 'permission' ? (prev.code === 'permission' ? prev.startTime : '') : ''),
                endTime: option.category === 'shift'
                  ? (option.endTime || '')
                  : (option.code === 'permission' ? (prev.code === 'permission' ? prev.endTime : '') : ''),
              }))}
              className={`rounded-2xl border px-4 py-3 text-left transition ${
                editor.code === option.code
                  ? `${option.bgClass} ring-2 ring-primary/20`
                  : 'border-slate-200 bg-white hover:bg-slate-50'
              }`}
            >
              <p className="text-sm font-semibold">{option.label}</p>
              <p className="mt-1 text-xs opacity-80">{option.category === 'shift' ? option.shortLabel : 'Estado especial'}</p>
            </button>
          ))}
        </div>

        {requiresTimeRange && (
          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-2">
              <span className="text-sm font-medium text-slate-700">Hora inicial</span>
              <input
                type="time"
                value={editor.startTime}
                onChange={(event) => setEditor((prev) => ({ ...prev, startTime: event.target.value }))}
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </label>
            <label className="space-y-2">
              <span className="text-sm font-medium text-slate-700">Hora final</span>
              <input
                type="time"
                value={editor.endTime}
                onChange={(event) => setEditor((prev) => ({ ...prev, endTime: event.target.value }))}
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </label>
          </div>
        )}

        <label className="space-y-2">
          <span className="text-sm font-medium text-slate-700">Observaciones</span>
          <textarea
            rows={3}
            value={editor.notes}
            onChange={(event) => setEditor((prev) => ({ ...prev, notes: event.target.value }))}
            className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
          />
        </label>
      </div>
    </ModalShell>
  );
};

const SettingsModal = ({ modal, shiftOptions, setModal, onClose, onSave, saving }) => (
  <ModalShell
    title="Configuración de usuario"
    subtitle={modal.user?.name || ''}
    onClose={onClose}
    footer={(
      <>
        <button
          onClick={onClose}
          className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
        >
          Cancelar
        </button>
        <button
          onClick={onSave}
          disabled={saving}
          className="rounded-2xl bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90 disabled:opacity-60"
        >
          {saving ? 'Guardando...' : 'Guardar restricciones'}
        </button>
      </>
    )}
  >
    <div className="space-y-5">
      <div className="grid gap-4 md:grid-cols-2">
        <ToggleField
          label="Puede hacer nocturno"
          value={modal.settings.canNightShift}
          onChange={(checked) => setModal((prev) => ({ ...prev, settings: { ...prev.settings, canNightShift: checked } }))}
        />
        <ToggleField
          label="Puede hacer 9 a 9"
          value={modal.settings.canNineToNine}
          onChange={(checked) => setModal((prev) => ({ ...prev, settings: { ...prev.settings, canNineToNine: checked } }))}
        />
        <ToggleField
          label="Puede trabajar domingo"
          value={modal.settings.canWorkSunday}
          onChange={(checked) => setModal((prev) => ({ ...prev, settings: { ...prev.settings, canWorkSunday: checked } }))}
        />
        <ToggleField
          label="Solo diurno"
          value={modal.settings.dayOnly}
          onChange={(checked) => setModal((prev) => ({ ...prev, settings: { ...prev.settings, dayOnly: checked } }))}
        />
      </div>

      <div>
        <p className="text-sm font-medium text-slate-700">Turnos permitidos</p>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          {shiftOptions.map((option) => {
            const checked = modal.settings.allowedShiftCodes.includes(option.code);
            return (
              <label key={option.code} className={`flex items-center gap-3 rounded-2xl border px-4 py-3 ${checked ? option.bgClass : 'border-slate-200 bg-white'}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => setModal((prev) => {
                    const current = prev.settings.allowedShiftCodes;
                    const next = current.includes(option.code)
                      ? current.filter((code) => code !== option.code)
                      : [...current, option.code];
                    return { ...prev, settings: { ...prev.settings, allowedShiftCodes: next } };
                  })}
                />
                <span className="text-sm font-medium">{option.label}</span>
              </label>
            );
          })}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <label className="space-y-2">
          <span className="text-sm font-medium text-slate-700">Día libre preferido</span>
          <select
            value={modal.settings.preferredDayOff}
            onChange={(event) => setModal((prev) => ({ ...prev, settings: { ...prev.settings, preferredDayOff: event.target.value } }))}
            className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
          >
            <option value="">Sin preferencia</option>
            <option value="Lunes">Lunes</option>
            <option value="Martes">Martes</option>
            <option value="Miércoles">Miércoles</option>
            <option value="Jueves">Jueves</option>
            <option value="Viernes">Viernes</option>
            <option value="Sábado">Sábado</option>
            <option value="Domingo">Domingo</option>
          </select>
        </label>
      </div>

      <label className="space-y-2">
        <span className="text-sm font-medium text-slate-700">Observaciones</span>
        <textarea
          rows={4}
          value={modal.settings.notes}
          onChange={(event) => setModal((prev) => ({ ...prev, settings: { ...prev.settings, notes: event.target.value } }))}
          className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
      </label>
    </div>
  </ModalShell>
);

const GlobalScheduleConfigModal = ({ modal, setModal, onClose, onSave, saving }) => (
  <ModalShell
    title="Configuración global de horarios"
    subtitle="Define etiquetas y turnos disponibles para todo el equipo."
    onClose={onClose}
    footer={(
      <>
        <button
          onClick={onClose}
          className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
        >
          Cancelar
        </button>
        <button
          onClick={onSave}
          disabled={saving}
          className="rounded-2xl bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90 disabled:opacity-60"
        >
          {saving ? 'Guardando...' : 'Guardar configuración'}
        </button>
      </>
    )}
  >
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 rounded-2xl border border-dashed border-primary/30 bg-primary/5 p-4">
        <div>
          <p className="text-sm font-semibold text-slate-800">Registros disponibles</p>
          <p className="text-xs text-slate-500">Agrega un turno o estado especial para todos los usuarios.</p>
        </div>
        <button
          type="button"
          onClick={() => setModal((prev) => ({
            ...prev,
            shiftOptions: [createCustomShiftOption(), ...prev.shiftOptions],
          }))}
          className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white transition hover:bg-primary/90"
        >
          <Plus size={16} />
          Nuevo registro
        </button>
      </div>

      {modal.shiftOptions.map((option, index) => {
        const isCustomOption = !DEFAULT_SHIFT_CODES.has(option.code);
        return (
          <div key={option.code} className="rounded-2xl border border-slate-200 p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <select
                value={option.category}
                disabled={!isCustomOption}
                onChange={(event) => setModal((prev) => {
                  const next = prev.shiftOptions.slice();
                  const category = event.target.value;
                  next[index] = {
                    ...next[index],
                    category,
                    startTime: category === 'shift' ? (next[index].startTime || '08:00') : '',
                    endTime: category === 'shift' ? (next[index].endTime || '17:00') : '',
                  };
                  return { ...prev, shiftOptions: next };
                })}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 disabled:cursor-not-allowed disabled:bg-slate-50"
              >
                <option value="shift">Turno</option>
                <option value="state">Estado especial</option>
              </select>
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2 text-xs font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={option.enabled !== false}
                    onChange={(event) => setModal((prev) => {
                      const next = prev.shiftOptions.slice();
                      next[index] = { ...next[index], enabled: event.target.checked };
                      return { ...prev, shiftOptions: next };
                    })}
                  />
                  Disponible
                </label>
                {isCustomOption && (
                  <button
                    type="button"
                    aria-label={`Eliminar ${option.label || 'registro'}`}
                    onClick={() => setModal((prev) => ({
                      ...prev,
                      shiftOptions: prev.shiftOptions.filter((item) => item.code !== option.code),
                    }))}
                    className="rounded-lg p-2 text-red-500 transition hover:bg-red-50 hover:text-red-700"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <label className="space-y-1.5">
                <span className="text-xs font-medium text-slate-700">Etiqueta</span>
                <input
                  value={option.label}
                  onChange={(event) => setModal((prev) => {
                    const next = prev.shiftOptions.slice();
                    next[index] = { ...next[index], label: event.target.value };
                    return { ...prev, shiftOptions: next };
                  })}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </label>
              <label className="space-y-1.5">
                <span className="text-xs font-medium text-slate-700">Etiqueta corta</span>
                <input
                  value={option.shortLabel}
                  onChange={(event) => setModal((prev) => {
                    const next = prev.shiftOptions.slice();
                    next[index] = { ...next[index], shortLabel: event.target.value };
                    return { ...prev, shiftOptions: next };
                  })}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </label>
            </div>

            {option.category === 'shift' && (
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <label className="space-y-1.5">
                  <span className="text-xs font-medium text-slate-700">Hora inicial</span>
                  <input
                    type="time"
                    value={option.startTime || ''}
                    onChange={(event) => setModal((prev) => {
                      const next = prev.shiftOptions.slice();
                      next[index] = { ...next[index], startTime: event.target.value };
                      return { ...prev, shiftOptions: next };
                    })}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </label>
                <label className="space-y-1.5">
                  <span className="text-xs font-medium text-slate-700">Hora final</span>
                  <input
                    type="time"
                    value={option.endTime || ''}
                    onChange={(event) => setModal((prev) => {
                      const next = prev.shiftOptions.slice();
                      next[index] = { ...next[index], endTime: event.target.value };
                      return { ...prev, shiftOptions: next };
                    })}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </label>
              </div>
            )}
          </div>
        );
      })}
    </div>
  </ModalShell>
);

const ToggleField = ({ label, value, onChange }) => (
  <label className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
    <span className="text-sm font-medium text-slate-700">{label}</span>
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`relative inline-flex h-7 w-12 items-center rounded-full transition ${
        value ? 'bg-primary' : 'bg-slate-300'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
          value ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  </label>
);

const HistoryModal = ({ schedule, onClose }) => (
  <ModalShell
    title="Historial de cambios"
    subtitle={schedule?.title || ''}
    onClose={onClose}
    footer={(
      <button
        onClick={onClose}
        className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
      >
        Cerrar
      </button>
    )}
  >
    <div className="space-y-3">
      {(schedule?.history || []).length ? (
        schedule.history
          .slice()
          .reverse()
          .map((entry) => (
            <div key={entry.id} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm font-semibold text-slate-900">{entry.summary}</p>
                <span className="text-xs text-slate-500">{new Date(entry.timestamp).toLocaleString()}</span>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                {entry.userName || 'Sistema'} · {entry.action}
              </p>
            </div>
          ))
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-6 text-sm text-slate-500">
          Todavía no hay cambios registrados.
        </div>
      )}
    </div>
  </ModalShell>
);

const PublishConfirmModal = ({ conflictingSchedule, onClose, onConfirm }) => (
  <ModalShell
    title="Confirmar publicación"
    subtitle="Solo puede haber una quincena publicada al mismo tiempo."
    onClose={onClose}
    footer={(
      <>
        <button
          onClick={onClose}
          className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
        >
          Cancelar
        </button>
        <button
          onClick={onConfirm}
          className="rounded-2xl bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90"
        >
          Publicar de todos modos
        </button>
      </>
    )}
  >
    <div className="space-y-3">
      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        Ya existe una quincena publicada. Si continúas, esa quincena pasará automáticamente a borrador.
      </div>
      {conflictingSchedule && (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
          <p className="text-sm font-semibold text-slate-900">{conflictingSchedule.title}</p>
          <p className="mt-1 text-xs text-slate-500">
            Estado actual: Publicado
          </p>
        </div>
      )}
    </div>
  </ModalShell>
);

export default Schedules;
